/**
 * Build your tiffin — the first step of a tiffin order.
 *
 * The plan first — the first day, the meal, how often it comes (daily,
 * bi-weekly, weekly or at weekends, as the kitchen offers) and for how many
 * days, at least the kitchen's minimum — then one box from the menu: any
 * items, as many of each as the kitchen allows. The box is for one person at
 * one meal; how many persons comes next. The figure in the footer is a
 * preview from the menu's own prices — the amount charged is the server's
 * quote on the next screens.
 *
 * Only what is served at the chosen meal is listed, and switching meal drops
 * anything from the box that the new meal does not serve.
 */

import { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  deliveryDates,
  formatDayRange,
  formatMeals,
  formatMoney,
  previewBox,
  tiffinEndDate,
  type MenuItem,
  type ServiceSlot,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { ArrowIcon } from '../../components/common/Icon';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { CountStepper } from '../../components/tiffin/CountStepper';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { DatePickerField } from '../../components/forms/DatePickerField';
import { EmptyState, ErrorState } from '../../components/feedback/States';
import { MenuItemCard } from '../../components/tiffin/MenuItemCard';
import { SchedulePicker } from '../../components/tiffin/SchedulePicker';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { SlotPicker } from '../../components/tiffin/SlotPicker';
import { Text } from '../../components/common/Text';
import { useAppConfig, useTiffinAvailability, useTiffinMenu } from '../../core/api/hooks';
import { useTiffinStore } from './tiffinStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function TiffinMenuScreen({ navigation }: DonorScreenProps<'TiffinMenu'>) {
  const t = useT();
  const pick = useLocalized();
  const config = useAppConfig();
  const menu = useTiffinMenu();
  const tiffin = config.data?.tiffin;

  const serviceDate = useTiffinStore((s) => s.serviceDate);
  const serviceSlot = useTiffinStore((s) => s.serviceSlot);
  const quantities = useTiffinStore((s) => s.quantities);
  const days = useTiffinStore((s) => s.days);
  const schedule = useTiffinStore((s) => s.schedule);
  const setDate = useTiffinStore((s) => s.setDate);
  const setSlot = useTiffinStore((s) => s.setSlot);
  const setDays = useTiffinStore((s) => s.setDays);
  const setSchedule = useTiffinStore((s) => s.setSchedule);
  const setQuantity = useTiffinStore((s) => s.setQuantity);
  const keepOnly = useTiffinStore((s) => s.keepOnly);
  const availability = useTiffinAvailability(serviceDate);

  // Today, on the kitchen's calendar, until the customer picks another day.
  useEffect(() => {
    if (tiffin && !serviceDate) setDate(tiffin.today);
  }, [tiffin, serviceDate, setDate]);

  // The kitchen's minimum run and its first schedule to start with, and back
  // inside what it offers if the operator has changed that since.
  useEffect(() => {
    if (!tiffin?.available) return;
    if (days === null || days < tiffin.minDays) setDays(tiffin.minDays);
    else if (days > tiffin.maxDays) setDays(tiffin.maxDays);
  }, [tiffin, days, setDays]);
  useEffect(() => {
    if (!tiffin?.available) return;
    const first = tiffin.schedules[0];
    if (first && (!schedule || !tiffin.schedules.includes(schedule))) setSchedule(first);
  }, [tiffin, schedule, setSchedule]);

  // The first meal still open that day, unless the chosen one still is.
  const slots = availability.data?.slots;
  useEffect(() => {
    if (!slots) return;
    const current = slots.find((slot) => slot.slot === serviceSlot);
    if (current?.open) return;
    const firstOpen = slots.find((slot) => slot.open);
    if (firstOpen) setSlot(firstOpen.slot);
  }, [slots, serviceSlot, setSlot]);

  const sections = useMemo(
    () =>
      (menu.data?.sections ?? [])
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => serviceSlot !== null && item.serviceSlots.includes(serviceSlot),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [menu.data, serviceSlot],
  );

  // Items the new meal does not serve leave the box when the meal changes.
  const offered = useMemo(() => sections.flatMap((section) => section.items), [sections]);
  useEffect(() => {
    if (menu.data && serviceSlot) keepOnly(offered.map((item) => item.id));
  }, [menu.data, offered, serviceSlot, keepOnly]);

  const byId = useMemo(() => new Map(offered.map((item) => [item.id, item])), [offered]);
  const lines = Object.entries(quantities)
    .map(([id, quantity]) => ({ item: byId.get(id), quantity }))
    .filter((line): line is { item: MenuItem; quantity: number } => Boolean(line.item));
  const count = lines.reduce((sum, line) => sum + line.quantity, 0);
  const box = lines.length ? previewBox(lines) : null;
  const slotOpen = Boolean(slots?.find((slot) => slot.slot === serviceSlot)?.open);
  const meals = serviceDate && days && schedule ? deliveryDates(serviceDate, days, schedule) : [];
  const canContinue = count > 0 && slotOpen && meals.length > 0;

  if (config.isError || menu.isError) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-tiffin-menu">
        <ErrorState
          onRetry={() => {
            void config.refetch();
            void menu.refetch();
          }}
        />
      </ScreenShell>
    );
  }

  if (tiffin && !tiffin.available) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-tiffin-menu">
        <EmptyState
          title={t('Tiffin ordering is not open yet')}
          message={t('We are setting up our tiffin service. Please check back soon, or contact us.')}
          actionLabel={t('Contact Us')}
          onAction={() => navigation.navigate('Contact')}
        />
      </ScreenShell>
    );
  }

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-menu"
      subHeader={
        <CheckoutHeader
          current={0}
          steps={TIFFIN_STEPS}
          title={t('Build Your Tiffin')}
          subtitle={t('Your plan, then what goes in each box')}
        />
      }
      pinnedFooter={
        <View style={styles.pinned}>
          <View style={styles.summary}>
            <View style={styles.summaryText}>
              <Text variant="caption" tone="tertiary">{t('Your box')}</Text>
              <Text variant="cardTitle" testID="tiffin-count">
                {count === 0
                  ? t('Add items from the menu')
                  : `${count} ${count === 1 ? t('item') : t('items')}`}
              </Text>
            </View>
            <View style={styles.totalBlock}>
              <Text variant="caption" tone="tertiary" align="right">{t('Per person, per meal')}</Text>
              <Text variant="amount" tone="brand" align="right" testID="tiffin-box-preview">
                {box ? formatMoney(box) : '—'}
              </Text>
            </View>
          </View>
          <Button
            label={t('Continue')}
            onPress={() => navigation.navigate('TiffinPersons')}
            disabled={!canContinue}
            trailingIcon={<ArrowIcon />}
            testID="cta-tiffin-continue"
          />
        </View>
      }
    >
      {tiffin ? (
        <View style={styles.block}>
          <Text variant="label" tone="secondary">{t('Starting')}</Text>
          <DatePickerField
            value={serviceDate}
            onChange={setDate}
            today={tiffin.today}
            minDaysAhead={0}
            maxDaysAhead={tiffin.maxDaysAhead}
            blackoutDates={[]}
            testID="tiffin-date"
          />
          <Text variant="caption" tone="secondary" testID="tiffin-min-days">
            {t('Tiffin orders run for at least {n} days in a row, from this day.').replace(
              '{n}',
              String(tiffin.minDays),
            )}
          </Text>
        </View>
      ) : (
        <Skeleton height={56} />
      )}

      {tiffin ? (
        <View style={styles.block}>
          <Text variant="label" tone="secondary">{t('Meal')}</Text>
          <SlotPicker
            windows={tiffin.slots}
            availability={slots}
            value={serviceSlot}
            onChange={(slot: ServiceSlot) => setSlot(slot)}
          />
          {slots && !slots.some((slot) => slot.open) ? (
            <Text variant="caption" tone="secondary" style={styles.hint} testID="tiffin-day-closed">
              {t('Every meal on this day has closed for orders. Choose another day.')}
            </Text>
          ) : null}
        </View>
      ) : null}

      {tiffin && serviceDate && schedule ? (
        <View style={styles.block}>
          <Text variant="label" tone="secondary">{t('How often')}</Text>
          <SchedulePicker
            offered={tiffin.schedules}
            start={serviceDate}
            value={schedule}
            onChange={setSchedule}
          />
        </View>
      ) : null}

      {tiffin && serviceDate && days !== null ? (
        <View style={[styles.block, styles.days]} testID="tiffin-days">
          <Text variant="label" tone="secondary">{t('For how many days?')}</Text>
          <CountStepper
            value={days}
            min={tiffin.minDays}
            max={tiffin.maxDays}
            onChange={setDays}
            unit={['Day', 'Days']}
            actions={['Fewer days', 'More days']}
            testID="days-stepper"
          />
          <Text variant="bodyStrong" tone="brand" align="center" testID="tiffin-plan-summary">
            {`${formatMeals(meals.length)} · ${formatDayRange(serviceDate, tiffinEndDate(serviceDate, days))}`}
          </Text>
        </View>
      ) : null}

      {tiffin ? (
        <Text variant="sectionTitle" style={styles.menuTitle}>{t('Fill your box')}</Text>
      ) : null}

      {menu.isLoading || (tiffin && !slots) ? (
        <View style={styles.loading}>
          <Skeleton height={96} />
          <Skeleton height={96} />
        </View>
      ) : !serviceSlot ? // No meal is open to choose; the line above has said why.
      null : sections.length === 0 ? (
        <Text variant="body" tone="secondary" style={styles.hint}>
          {t('Nothing is on the menu for this meal yet.')}
        </Text>
      ) : (
        sections.map((section) => (
          <View key={section.id} style={styles.section} testID={`menu-section-${section.id}`}>
            <View style={styles.sectionHeader}>
              <Text variant="sectionTitle">{pick(section.name, section.nameUr)}</Text>
              {section.description ? (
                <Text variant="caption" tone="tertiary">
                  {pick(section.description, section.descriptionUr)}
                </Text>
              ) : null}
            </View>
            <View style={styles.items}>
              {section.items.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  quantity={quantities[item.id] ?? 0}
                  onChange={(quantity) => setQuantity(item.id, quantity)}
                />
              ))}
            </View>
          </View>
        ))
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  block: {
    gap: space.sm,
    marginBottom: space.lg,
  },
  hint: {
    marginTop: space.sm,
  },
  days: {
    padding: space.md,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
  },
  menuTitle: {
    marginBottom: space.md,
  },
  loading: {
    gap: space.md,
  },
  section: {
    gap: space.md,
    marginBottom: space.xl,
  },
  sectionHeader: {
    gap: space.xxs,
  },
  items: {
    gap: space.sm,
  },
  pinned: {
    gap: space.md,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.md,
  },
  summaryText: {
    flex: 1,
    gap: space.xxs,
  },
  totalBlock: {
    alignItems: 'flex-end',
    gap: space.xxs,
  },
});
